/**
 * Écran Membres : qui travaille ici, et comment faire entrer quelqu'un.
 *
 * Trois partis pris.
 *
 * 1. **Le lien d'invitation s'affiche une seule fois, et l'écran le dit.** Il n'est pas
 *    stocké en clair — la base n'en garde que le condensat —, donc on ne peut pas le
 *    réafficher plus tard. Une interface qui laisserait croire l'inverse produirait la
 *    pire des situations : quelqu'un qui ferme le bandeau et revient le chercher.
 * 2. **Le rôle se change sur place, dans la ligne.** Ouvrir une fenêtre pour un menu à
 *    trois entrées ajoute deux clics à une action qu'un administrateur fait le jour où il
 *    monte son équipe, puis presque jamais.
 * 3. **Le refus du dernier administrateur vient du serveur**, et son message est affiché
 *    tel quel. Le dupliquer ici donnerait deux vérités à maintenir, et c'est celle du
 *    serveur qui compte.
 */

import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  Copy,
  Mail,
  ShieldCheck,
  Trash2,
  UserPlus,
} from "lucide-react";

import {
  ROLES,
  ROLE_HINTS,
  ROLE_LABELS,
  createInvitation,
  frenchDate,
  invitationLink,
  listInvitations,
  listMembers,
  removeMember,
  revokeInvitation,
  setRole,
  type Invitation,
  type IssuedInvitation,
  type Member,
} from "./membersApi";
import { useSession } from "./ui/session";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  Select,
  Skeleton,
  Td,
  Th,
} from "./ui/primitives";

function RoleBadge({ role }: { role: string }) {
  return (
    <Badge tone={role === "admin" ? "accent" : "neutral"}>
      {ROLE_LABELS[role] ?? role}
    </Badge>
  );
}

/** Le lien fraîchement émis. Volontairement encombrant : il ne réapparaîtra pas. */
function IssuedLink({
  issued,
  onDone,
}: {
  issued: IssuedInvitation;
  onDone: () => void;
}) {
  const link = invitationLink(issued.token);
  const [copied, setCopied] = useState(false);

  return (
    <Card className="border-good/40 bg-good-soft px-4 py-3">
      <div className="text-[0.88rem] font-semibold text-good">
        Invitation prête pour {issued.email}
      </div>
      <p className="mt-1 text-[0.8rem] text-muted">
        Transmettez ce lien à la personne concernée. Il vaut{" "}
        {ROLE_LABELS[issued.role]?.toLowerCase() ?? issued.role} jusqu'au{" "}
        {frenchDate(issued.expires_at)}, et{" "}
        <strong>ne sera plus affiché</strong> — seule son empreinte est
        conservée.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-md bg-surface px-2 py-1.5 font-mono text-[0.75rem] text-ink">
          {link}
        </code>
        <Button
          variant="primary"
          onClick={() => {
            void navigator.clipboard.writeText(link).then(() => {
              setCopied(true);
            });
          }}
        >
          <Copy className="h-3.5 w-3.5" />
          {copied ? "Copié" : "Copier"}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Fermer
        </Button>
      </div>
    </Card>
  );
}

export function MembersView() {
  const { identity } = useSession();
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRoleChoice] = useState<string>("engineer");
  const [issued, setIssued] = useState<IssuedInvitation | null>(null);

  const admin = identity?.role === "admin";

  const reload = useCallback(() => {
    setError(null);
    return Promise.all([
      listMembers(),
      // Un technicien n'a pas accès aux invitations : le serveur répondrait 403, et un
      // écran qui affiche une erreur pour une section qu'il ne devrait pas voir donne
      // l'air d'être cassé. On ne la demande donc pas.
      admin ? listInvitations() : Promise.resolve<Invitation[]>([]),
    ])
      .then(([m, i]) => {
        setMembers(m);
        setInvitations(i);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "erreur inconnue");
      })
      .finally(() => setLoading(false));
  }, [admin]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const inviter = useCallback(() => {
    const propre = email.trim();
    if (!propre) return;
    setBusy(true);
    setError(null);
    createInvitation(propre, role)
      .then((fresh) => {
        setIssued(fresh);
        setEmail("");
        return reload();
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "erreur inconnue");
      })
      .finally(() => setBusy(false));
  }, [email, reload, role]);

  const agir = useCallback(
    (action: Promise<void>) => {
      setBusy(true);
      setError(null);
      action
        .then(() => reload())
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : "erreur inconnue");
        })
        .finally(() => setBusy(false));
    },
    [reload],
  );

  if (loading) return <Skeleton className="h-64 w-full" />;

  const enAttente = invitations.filter((i) => i.pending);

  return (
    <div className="space-y-3">
      <div className="mb-4">
        <h1 className="text-xl font-semibold tracking-tight">Membres</h1>
        <p className="mt-1 max-w-2xl text-[0.88rem] text-muted">
          Les personnes qui accèdent aux données de cette organisation. Un
          compte appartient à une seule organisation : inviter quelqu'un le fait
          entrer ici, pas ailleurs.
        </p>
      </div>

      {error && (
        <Card className="border-bad/40 bg-bad-soft px-4 py-3">
          <div className="flex gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-bad" />
            <div className="text-[0.82rem] text-muted">{error}</div>
          </div>
        </Card>
      )}

      {issued && <IssuedLink issued={issued} onDone={() => setIssued(null)} />}

      {admin && (
        <Card>
          <CardHeader
            title={
              <span className="flex items-center gap-1.5">
                <UserPlus className="h-4 w-4 text-muted" />
                Inviter quelqu'un
              </span>
            }
            hint="Aucun courriel n'est envoyé : vous obtenez un lien à transmettre."
          />
          <div className="flex flex-wrap items-end gap-3 px-4 py-4">
            <label className="min-w-0 flex-1">
              <span className="mb-1 block text-[0.72rem] tracking-wide text-faint uppercase">
                Adresse
              </span>
              <Input
                type="email"
                value={email}
                placeholder="prenom.nom@usine.fr"
                className="w-full"
                disabled={busy}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") inviter();
                }}
              />
            </label>
            <label>
              <span className="mb-1 block text-[0.72rem] tracking-wide text-faint uppercase">
                Rôle
              </span>
              <Select
                value={role}
                disabled={busy}
                onChange={(e) => setRoleChoice(e.target.value)}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </Select>
            </label>
            <Button
              variant="primary"
              size="md"
              disabled={busy || !email.trim()}
              onClick={inviter}
            >
              <Mail className="h-4 w-4" />
              Créer l'invitation
            </Button>
          </div>
          <p className="border-t border-line px-4 py-2.5 text-[0.78rem] text-muted">
            {ROLE_HINTS[role]}
          </p>
        </Card>
      )}

      <Card className="overflow-hidden">
        <CardHeader
          title={
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4 text-muted" />
              {members.length} membre(s)
            </span>
          }
          hint="Un rôle se change sur place. Le dernier administrateur ne peut pas être retiré."
        />
        <table className="w-full border-collapse text-[0.82rem]">
          <thead>
            <tr>
              <Th>Personne</Th>
              <Th>Rôle</Th>
              <Th>Depuis</Th>
              <Th>Dernière visite</Th>
              {admin && <Th> </Th>}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.user_id}>
                <Td>
                  <div className="font-medium">{m.full_name ?? m.email}</div>
                  {m.full_name && (
                    <div className="text-[0.75rem] text-faint">{m.email}</div>
                  )}
                </Td>
                <Td>
                  {admin ? (
                    <Select
                      value={m.role}
                      disabled={busy}
                      onChange={(e) => agir(setRole(m.user_id, e.target.value))}
                      className="w-40"
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABELS[r]}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <RoleBadge role={m.role} />
                  )}
                </Td>
                <Td className="text-muted">{frenchDate(m.joined_at)}</Td>
                <Td className="text-muted">
                  {m.last_login_at ? (
                    frenchDate(m.last_login_at)
                  ) : (
                    <span className="text-faint">jamais venu</span>
                  )}
                </Td>
                {admin && (
                  <Td className="text-right">
                    <Button
                      variant="ghost"
                      disabled={busy}
                      onClick={() => agir(removeMember(m.user_id))}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Retirer
                    </Button>
                  </Td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {admin && (
          <p className="border-t border-line px-4 py-2.5 text-[0.76rem] text-faint">
            Retirer un membre supprime son accès, pas son compte ni ses dépôts —
            un journal d'audit qui désigne un utilisateur inexistant ne prouve
            plus rien.
          </p>
        )}
      </Card>

      {admin && enAttente.length > 0 && (
        <Card className="overflow-hidden">
          <CardHeader
            title="Invitations en attente"
            hint="Le lien a été remis à l'émission. Révoquer le ferme immédiatement."
          />
          <table className="w-full border-collapse text-[0.82rem]">
            <thead>
              <tr>
                <Th>Adresse</Th>
                <Th>Rôle</Th>
                <Th>Expire</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {enAttente.map((i) => (
                <tr key={i.id}>
                  <Td className="font-medium">{i.email}</Td>
                  <Td>
                    <RoleBadge role={i.role} />
                  </Td>
                  <Td className="text-muted">{frenchDate(i.expires_at)}</Td>
                  <Td className="text-right">
                    <Button
                      variant="ghost"
                      disabled={busy}
                      onClick={() => agir(revokeInvitation(i.id))}
                    >
                      Révoquer
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
